import React, { useEffect, useRef } from "react";
import type { UIMessage } from "ai";
import type { SlashCommand } from "../types";
import { MessageBubble } from "./MessageBubble";
import { EmptyState } from "./EmptyState";

interface MessageListProps {
  messages: UIMessage[];
  isStreaming?: boolean;
  suggestions?: SlashCommand[];
  onSuggestionClick?: (cmd: SlashCommand) => void;
}

/** Check if the last assistant message has any text content yet */
function hasAssistantContent(messages: UIMessage[]): boolean {
  const last = messages[messages.length - 1];
  if (!last || last.role !== "assistant") return false;
  return last.parts.some((p) => p.type === "text" && p.text.length > 0);
}

export function MessageList({
  messages,
  isStreaming,
  suggestions,
  onSuggestionClick,
}: MessageListProps) {
  const bottomRef = useRef<HTMLDivElement>(null);

  // Scroll on new messages and during streaming
  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages]);

  if (messages.length === 0 && !isStreaming) {
    return <EmptyState suggestions={suggestions} onSuggestionClick={onSuggestionClick} />;
  }

  return (
    <div className="flex-1 overflow-y-auto px-4 py-4 cai-scrollbar relative z-[1]" role="log" aria-label="Chat messages" aria-live="polite">
      {messages.map((msg) => (
        <MessageBubble key={msg.id} message={msg} />
      ))}

      {/* Thinking indicator — shown when streaming but no assistant text yet */}
      {isStreaming && !hasAssistantContent(messages) && (
        <div className="flex gap-2.5 mb-4">
          <div className="w-6 h-6 cai-avatar cai-avatar-thinking rounded-full shrink-0 mt-0.5" />
          <div className="cai-glass px-3.5 py-3 rounded-2xl rounded-tl-sm">
            <div className="flex items-center gap-2.5">
              <div className="flex gap-1.5">
                <span className="w-1.5 h-1.5 bg-accent-violet rounded-full" style={{ animation: "cai-dot-pulse 1.2s ease-in-out infinite", animationDelay: "0ms" }} />
                <span className="w-1.5 h-1.5 bg-accent-pink rounded-full" style={{ animation: "cai-dot-pulse 1.2s ease-in-out infinite", animationDelay: "200ms" }} />
                <span className="w-1.5 h-1.5 bg-accent-cyan rounded-full" style={{ animation: "cai-dot-pulse 1.2s ease-in-out infinite", animationDelay: "400ms" }} />
              </div>
              <span className="text-[11px] text-text-secondary">Thinking...</span>
            </div>
          </div>
        </div>
      )}

      <div ref={bottomRef} />
    </div>
  );
}
